import { useMemo } from 'react';
import { hasSameEffectiveDate } from '~/modules/rateCard/advancedRateCard/components/hooks/utils';

const NO_ERRORS = {};
const EFFECTIVE_DATE_FIELD = 'effectiveDate';
const ERROR_MARKER = true;

export const buildDimensionRequiredErrors = ({
  selectedDimensions,
  projectRates
}) => {
  const dimensionErrors = {};

  projectRates.forEach((rate, index) => {
    selectedDimensions.forEach(dimension => {
      const cell = rate[dimension.id];

      if (cell && !cell.value) {
        dimensionErrors[dimension.id] = {
          ...dimensionErrors[dimension.id],
          [index]: ERROR_MARKER
        };
      }
    });
  });

  return dimensionErrors;
};

export const buildEffectiveDateRequiredErrors = ({ projectRates }) => {
  const dateErrors = {};

  projectRates.forEach((rate, index) => {
    if (!rate.isInitialRate && !rate.effectiveDate) {
      dateErrors[index] = ERROR_MARKER;
    }
  });

  return dateErrors;
};

const defaultDuplicateMessage = () => ERROR_MARKER;

const formatDuplicateMessage = (formatMessage, field) =>
  formatMessage
    ? formatMessage({ id: 'advancedRateCard.validations.duplicate' }, { field })
    : ERROR_MARKER;

const buildDuplicateMessageGetter = formatMessage => dimension =>
  formatDuplicateMessage(formatMessage, dimension.displayText);

const buildEffectiveDateDuplicateMessageGetter = formatMessage => () =>
  formatDuplicateMessage(
    formatMessage,
    formatMessage && formatMessage({ id: 'advancedRateCard.effectiveDate' })
  );

const groupRatesByGroupId = projectRates => {
  const ratesByGroup = {};

  projectRates.forEach(rate => {
    if (!ratesByGroup[rate.groupId]) {
      ratesByGroup[rate.groupId] = [];
    }

    ratesByGroup[rate.groupId].push(rate);
  });

  return ratesByGroup;
};

export const buildDuplicateEffectiveDateErrors = ({
  projectRates,
  getDuplicateMessage = defaultDuplicateMessage
}) => {
  const dateErrors = {};
  const ratesByGroup = groupRatesByGroupId(projectRates);

  projectRates.forEach((rate, index) => {
    if (
      hasSameEffectiveDate(
        rate.isInitialRate,
        rate.effectiveDate,
        ratesByGroup[rate.groupId] || [],
        rate
      )
    ) {
      dateErrors[index] = getDuplicateMessage();
    }
  });

  return dateErrors;
};

const isDuplicateDimensionValue = (
  projectRates,
  currentRate,
  dimensionId,
  previousDimensionId
) =>
  Boolean(currentRate[dimensionId]?.value) &&
  projectRates.some(
    rate =>
      rate.id !== currentRate.id &&
      rate[dimensionId]?.id !== currentRate[dimensionId]?.id &&
      (!previousDimensionId ||
        rate[previousDimensionId]?.id ===
          currentRate[previousDimensionId]?.id) &&
      rate[dimensionId]?.value?.id === currentRate[dimensionId]?.value?.id
  );

export const buildDuplicateDimensionErrors = ({
  selectedDimensions,
  projectRates,
  getDuplicateMessage = defaultDuplicateMessage
}) => {
  const dimensionErrors = {};

  projectRates.forEach((rate, index) => {
    selectedDimensions.forEach((dimension, dimensionIndex) => {
      if (
        isDuplicateDimensionValue(
          projectRates,
          rate,
          dimension.id,
          selectedDimensions[dimensionIndex - 1]?.id
        )
      ) {
        dimensionErrors[dimension.id] = {
          ...dimensionErrors[dimension.id],
          [index]: getDuplicateMessage(dimension)
        };
      }
    });
  });

  return dimensionErrors;
};

const mergeDimensionErrors = (base, extra) => {
  const merged = { ...base };

  Object.keys(extra).forEach(dimensionId => {
    merged[dimensionId] = { ...merged[dimensionId], ...extra[dimensionId] };
  });

  return merged;
};

export const useProjectCostOverrideErrors = ({
  selectedDimensions,
  projectRates,
  formatMessage
}) =>
  useMemo(() => {
    const requiredDimensionErrors = buildDimensionRequiredErrors({
      selectedDimensions,
      projectRates
    });

    const duplicateDimensionErrors = buildDuplicateDimensionErrors({
      selectedDimensions,
      projectRates,
      getDuplicateMessage: buildDuplicateMessageGetter(formatMessage)
    });

    const dimensionErrors = mergeDimensionErrors(
      requiredDimensionErrors,
      duplicateDimensionErrors
    );

    const effectiveDateErrors = {
      ...buildEffectiveDateRequiredErrors({ projectRates }),
      ...buildDuplicateEffectiveDateErrors({
        projectRates,
        getDuplicateMessage: buildEffectiveDateDuplicateMessageGetter(
          formatMessage
        )
      })
    };

    const projectRateErrors = {
      ...dimensionErrors,
      ...(Object.keys(effectiveDateErrors).length > 0 && {
        [EFFECTIVE_DATE_FIELD]: effectiveDateErrors
      })
    };

    const hasErrors = Object.keys(projectRateErrors).length > 0;

    return {
      errors: hasErrors ? { projectRates: projectRateErrors } : NO_ERRORS,
      hasErrors
    };
  }, [selectedDimensions, projectRates, formatMessage]);

export default useProjectCostOverrideErrors;
